import { useQueryClient } from '@tanstack/react-query';
import { first } from 'es-toolkit/compat';
import { useEffect } from 'react';
import { useSessionStorage } from 'shared/hooks/useSessionStorage.js';
import { useEventListener } from 'usehooks-ts';

import { useMyUser } from './getMyUser.js';
import type { CompanyContextProps } from './useCurrentCompany.js';

export const useCompanyContext = (): CompanyContextProps => {
  const { user } = useMyUser();
  const [selectedCompany, setSelectedCompany] = useSessionStorage<
    string | undefined
  >('currentCompany', undefined);
  const queryClient = useQueryClient();

  useEventListener('session-storage', () => void queryClient.resetQueries());

  useEffect(() => {
    if (!selectedCompany && !user?.isRegistryAdmin && user?.companies?.[0]?.id) {
      setSelectedCompany(user?.companies?.[0]?.id);
    }
  }, [user?.isRegistryAdmin, user?.companies?.length]);

  if (user?.isRegistryAdmin) {
    return {
      companyId: null,
      companyName: null,
      setSelectedCompany: null,
    };
  }

  const company
    = user?.companies?.find(company => company.id === selectedCompany)
      ?? first(user?.companies);

  if (!company) {
    return {
      companyId: undefined,
      companyName: undefined,
      setSelectedCompany: undefined,
    };
  }

  return {
    companyId: company.id,
    companyName: company.name,
    setSelectedCompany: companyId => setSelectedCompany(companyId),
  };
};
